/**
 * utilityFunctions.js
 * Created by calhosh on 8/27/2020.
 */

import React from 'react';
import {currentResource, getFilteredQueryParameter, isNullOrUndefined, labels, numberFormattedText} from './utils';
import queryString from 'query-string';
import {
	canvasWebRoutes,
	canvasUrlParams,
	documentTypes,
	DocumentStatus,
	ContentContract,
	PaperProfileIDs,
	serviceURL,
	entities,
	Entity,
	dateFormats,
	defaultDateFormat,
	OpenInCanvasDocumentTypeIDs,
	EngagementCommentStatus,
	documentNameMaxLength,
	OfflineTaskListFilters,
	taskApiRoutes,
	relateDocumentsTo,
	smartScreenRoutes,
	EngagementStatus,
	aresTaskViewRoutes,
	AccountType,
	fileExtensionMapping,
	actionType,
	actionTags,
	validationFilter,
	SmartAutomationEvidenceIDs,
	documentCategoryNames,
	GuidedWorkflowV2DocumentTypes,
	CanvasFormsIds,
	documentSources,
	formBodyType,
	CanvasFormsWithoutNavigationPanel,
	ClassicSummaryScreenDocumentTypes,
	StatementType,
	AssertionIds,
	riskTypes,
	riskTypeList,
	documentTabLayoutTabs,
	documentWithoutTabLayout,
	helixImportStatus,
	ScotType,
	assertionNameMapper,
	assertionsFormat,
	RightsAndObligations,
	SortDirection,
	controlDeficiencyType
} from './uiconstants';
import moment from 'moment';
import env from '../util/env';
import _ from 'lodash';
import {getValidationCountForIds} from '../components/Common/CanvasForm/Common/ValidationUtility';
import {decodeURIComponentSafe} from '../components/Common/EngagementComment/Utility/commentsUtilityFunctions';
import {applyRound} from '@ey/utils';
import DOMPurify from 'dompurify';
import {matchRoutes} from 'react-router';
import parse from 'html-react-parser';
import MotifButton from '@ey/motif-react-wrapper/Button';
import DocHubClient from './doc-hub-client';

let dragAndDropUploadObject = [];
let locationParentObject = window;

class Utility extends Object {
	static getPathObject(url) {
		const {pathname, search} = new URL(url);

		return {
			pathname,
			search
		};
	}

	static getLocation() {
		return locationParentObject.location;
	}

	static cfAlphanum = (a, b) => a?.localeCompare(b, undefined, {sensitivity: 'base', numeric: true});

	static getCanvasBaseUrl = (engagement, displayLanguage) => {
		let curl = env.getURL('eng_web') + '/pages/index/index.html?';
		curl = `${curl}countryid=${engagement.countryId}&`;
		curl = `${curl}engagementid=${engagement.engagementId}&`;
		curl = engagement.clientid ? `${curl}clientid=${engagement.clientid}` : curl;
		curl = engagement.clientname ? `${curl}clientname=${encodeURIComponent(engagement.clientname)}` : curl;
		curl = `${curl}workspacedesc=${encodeURIComponent(engagement.workspaceDescription)}&`;
		curl = `${curl}workspaceid=${engagement.workspaceId}&`;
		curl = `${curl}userPrefLang=${displayLanguage ? displayLanguage : ''}&`;
		curl = `${curl}engagementversion=${engagement.engagementVersion}`;
		return curl;
	};

	static getGuidedWorkflowUrl = (engagementId, documentId, taskId, headerId, sectionId) => {
		let url = `/${aresTaskViewRoutes.guidedworkflowV2}/${documentId}`;

		if (headerId) {
			url += `/${aresTaskViewRoutes.header}/${headerId}`;
		}

		if (sectionId) {
			url += `/${aresTaskViewRoutes.section}/${sectionId}`;
		}

		url += `?${canvasUrlParams.engagementid}=${engagementId}`;

		if (taskId) {
			url += `&taskId=${taskId}`;
		}

		return url;
	};

	static getAutomationHubLink = (baseCanvasUrl, engagement, engagements, isAutomationHubEnabled) => {
		if (isAutomationHubEnabled === false || !engagements || engagements.length === 0) {
			return baseCanvasUrl + '#automationhub';
		} else {
			if (engagements && engagements.length > 0) {
				const thisEngagement = engagements.find((emd) => emd.engagementid === engagement.engagementId);
				return `${env.getURL('Smart_Automation_Url')}?engagementid=${engagement.engagementId}&countryId=${engagement.countryId}&dataCenterId=${
					thisEngagement?.datacenterid
				}`;
			}
		}
	};

	static getAutomationHubUrlFromFeature = (path = '', features) => {
		if (features?.eySmartAutomationBase) {
			return `${_.trimEnd(features?.eySmartAutomationBase, '/')}${path}`;
		} else {
			env.getSmartAutomationUrl(path);
		}
	};

	static getHelixPrimaryAnalyzer = (engagementHelixProjects, helixAnalyzerType) => {
		if (engagementHelixProjects && engagementHelixProjects?.length > 0) {
			const primaryAnalyzer = engagementHelixProjects.find((project) => project.isPrimary && project.helixImportStatusID === helixImportStatus.importCompleted);
			if (primaryAnalyzer) {
				return primaryAnalyzer?.helixAnalyzerType?.toLowerCase() === helixAnalyzerType?.toLowerCase() ? primaryAnalyzer : false;
			}
		}

		return false;
	};

	static isISOFormat(dateString) {
		return dateString.length === 24 && dateString.endsWith('Z');
	}

	static isValidDate(dateString) {
		var date = new Date(dateString);
		return !isNaN(date);
	}

	static getJsonPatchDocumentElement(operation, property, propertyValue) {
		return {op: operation, path: property, value: propertyValue};
	}

	static getErrorMessageFromResponse(responseMessage) {
		let error;
		try {
			if (responseMessage && responseMessage.indexOf(' - ') > 0) {
				error = responseMessage.substr(0, responseMessage.indexOf(' -'));
			}
		} catch (err) {
			/* empty */
		}
		if (error && error.length > 0 && !Array.isArray(error)) {
			if (!error.ErrorCode) {
				const errorValue = Number(error);
				if (!isNaN(errorValue)) {
					return currentResource.Errors[errorValue] ? error + ': ' + currentResource.Errors[errorValue] : responseMessage;
				}
			}
		}
		return responseMessage;
	}

	static getEngagementIdFromUrl(history) {
		const queryStringParams = queryString.parse(history.location.search.toLowerCase());
		const engId = queryStringParams.engagementid;

		// If the engagementId is sent multiple times in the url, keep the first one
		let engagementId = engId;
		if (engId && (Array.isArray(engId) || engId.indexOf(',') >= 0)) {
			engagementId = Array.isArray(engId) ? engId[0] : engId.split(',')[0];
			if (engagementId.indexOf(',') >= 0) {
				engagementId = engagementId.split(',')[0];
			}
		}

		return engagementId;
	}

	static setLocationParentObject(l) {
		locationParentObject = l;
	}

	static getDocumentIdFromUrl(history) {
		// Early return for invalid history object
		if (!history || history.location === undefined) {
			return null;
		}

		// Prefer locationParentObject when history is window
		// to get better location info when in MFE mode
		// (this check is needed in MFE mode; standalone mode will always end up using window.location)
		const locationSource = history === window ? locationParentObject || history : history || locationParentObject;

		// Try to get documentId from query string parameters first
		const documentIdFromQuery = this._getDocumentIdFromQueryString(locationSource);
		if (documentIdFromQuery) {
			return documentIdFromQuery;
		}

		// Fallback to extracting documentId from URL path
		return this._getDocumentIdFromUrlPath(locationSource);
	}

	/**
	 * Extracts document ID from query string parameters
	 * @private
	 * @param {Object} locationSource - Location object containing search parameters
	 * @returns {string|null} Document ID if found and valid, null otherwise
	 */
	static _getDocumentIdFromQueryString(locationSource) {
		const queryStringParams = queryString.parse(locationSource?.location?.search?.toLowerCase());
		let documentId = queryStringParams.documentid;

		if (Array.isArray(documentId)) {
			documentId = documentId[0];
		}

		return this.validateGUID(documentId) ? documentId?.toLowerCase() : null;
	}

	/**
	 * Extracts document ID from URL path segments
	 * @private
	 * @param {Object} locationSource - Location object containing href
	 * @returns {string|null} Document ID if found and valid, null otherwise
	 */
	static _getDocumentIdFromUrlPath(locationSource) {
		const locationSplit = locationSource?.location?.href?.split('/');

		if (!locationSplit || locationSplit.length < 5) {
			return null;
		}

		// Check position 4 in URL path (0-indexed)
		const candidateAtIndex4 = locationSplit[4]?.split('?')[0];
		if (this.validateGUID(candidateAtIndex4)) {
			return candidateAtIndex4.toLowerCase();
		}

		// Check position 5 in URL path (0-indexed)
		if (locationSplit.length > 5) {
			const candidateAtIndex5 = locationSplit[5]?.split('?')[0];
			if (this.validateGUID(candidateAtIndex5)) {
				return candidateAtIndex5.toLowerCase();
			}
		}

		// Check position 6 in URL path (0-indexed)
		if (locationSplit.length > 6) {
			const candidateAtIndex6 = locationSplit[6]?.split('?')[0];
			if (this.validateGUID(candidateAtIndex6)) {
				return candidateAtIndex6.toLowerCase();
			}
		}

		return null;
	}

	/**
	 * validates guid id.
	 * @param {Guid} guid - guid id.
	 * @returns {bool} validates guid.
	 * */
	static validateGUID(guid) {
		let isGUIDValid = false;
		const pattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/i;
		if (pattern.test(guid)) {
			isGUIDValid = true;
		}

		return isGUIDValid;
	}

	static isEmptyGuid(value) {
		return _.isEmpty(value) || value === '00000000-0000-0000-0000-000000000000';
	}

	static parseRichText(text) {
		if (!text || typeof text !== 'string') {
			return text;
		}

		try {
			const markedText = Utility.createMarkup(text);
			if (!markedText || !markedText.__html) {
				return text;
			}

			return parse(markedText.__html);
		} catch (error) {
			console.warn('Error parsing rich text:', error);
			return Utility.createRichTextElement(text);
		}
	}

	static genericCollectionSort = (list, key, order = SortDirection.SORT_ASC) => {
		if (list.length) {
			switch (typeof list[0][key]) {
				case 'string':
					return list.sort((a, b) => {
						const av = a && a[key] != null ? String(a[key]).trim() : '';
						const bv = b && b[key] != null ? String(b[key]).trim() : '';
						if (order === SortDirection.SORT_ASC) {
							return av.localeCompare(bv);
						}
						return bv.localeCompare(av);
					});
				default:
					return list.sort((a, b) => {
						if (order === SortDirection.SORT_ASC) {
							return a[key] - b[key];
						}
						return b[key] - a[key];
					});
			}
		}
		return list;
	};
	/**
	 * sort an array by given keys.
	 * @param {Array} toSort - the array to sort.
	 * @returns {any} the sortedArray
	 * */
	static sortByMultipleKey(toSort) {
		let key = '';

		// check if toSort is array
		if (Array.isArray(toSort)) {
			if (toSort.length === 0) return 0;
			key = toSort[0];
		} else {
			key = toSort;
		}

		return function (a, b) {
			let valueA = a[key];
			let valueB = b[key];

			// Both values are strings. Compare them in a case-insensitive manner.
			if (typeof valueA === 'string' && typeof valueB === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			// nulls sort after anything else
			if (valueA === null) {
				return 1;
			} else if (valueB === null) {
				return -1;
			}
			// otherwise, if we're ascending, lowest sorts first
			else if (valueA < valueB) {
				return -1;
			}
			// if descending, highest sorts first
			else if (valueA > valueB) {
				return 1;
			}
			// equal items sort equally
			else {
				if (toSort.slice(1).length > 0) {
					return Utility.sortByMultipleKey(toSort.slice(1))(a, b);
				} else return 0;
			}
		};
	}

	static sortAlphaNumeric(arr = [], sortKey) {
		const compareAlphaNumeric = (a, b) => {
			let valueA = a[sortKey];
			let valueB = b[sortKey];

			// Both values are strings. Compare them in a case-insensitive manner.
			if (typeof valueA === 'string' && typeof valueB === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA > valueB) return 1;
			if (valueA < valueB) return -1;
			return 0;
		};
		const compareNumeric = (a, b) => {
			if (a > b) return 1;
			if (a < b) return -1;
			return 0;
		};
		arr.sort(sortKey ? compareAlphaNumeric : compareNumeric);
	}

	/**
	 * Sort an Array based on a key.
	 * @param {Array} arr - Array to sort.
	 * @param {String} sortKey - Property name to sort the array.
	 * @returns {Array} A new array sorted by the given key.
	 * */

	static sortAlphaNumericImmutable(arr = [], sortKey) {
		const compareAlphaNumeric = (a, b) => {
			let valueA = a[sortKey];
			let valueB = b[sortKey];

			// Both values are strings. Compare them in a case-insensitive manner.
			if (typeof valueA === 'string' && typeof valueB === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			if (valueA > valueB) return 1;
			if (valueA < valueB) return -1;
			return 0;
		};
		const compareNumeric = (a, b) => {
			if (a > b) return 1;
			if (a < b) return -1;
			return 0;
		};
		return [...arr].sort(sortKey ? compareAlphaNumeric : compareNumeric);
	}

	/**
	 * Determines whether the components are being rendered in server-side mode.
	 * @returns {string} true if the components are being rendered as server-side; otherwise false.
	 * */
	static canUseDOM() {
		return typeof window !== 'undefined' && (typeof process === 'undefined' || process.browser);
	}

	/**
	 * Creates a rich text element to show rich text. Eg. (Bold, Italic, Underline...)
	 * rastwe: TODO: need some justification for having this here. Kendo editor?
	 * @param {string} text - the text to display
	 * @param {string} className - the className to display
	 * @param {function} onCLick - on click function
	 * @returns {Object} a rich text element to display
	 * */
	static createRichTextElement(text, className, onCLick) {
		if (this.canUseDOM() !== true && text) {
			return (
				<span
					onClick={(e) => {
						onCLick && onCLick(e);
					}}
					className={`createRichTextElementSpanWrapper ${className ? className : ''}`}
					dangerouslySetInnerHTML={Utility.createMarkup(text)}
				/>
			);
		} else if (text) {
			/*having this breaks kendo. We dont want to sanitize what the users enter into kendo.
			E.g if I enter this:
			test <script>alert('hi');</script> test
			<span class="info-test">engagement</span>
			<b>bold text</b>
			<a>anchor tag</a>
			<u>underlined</u>
			<i>italic</>
			<strong><span style="text-decoration: underline;"><em>Text description</em></span></strong>
			I want to see this as I wrote it NOT test test and actual bold text or anchor links.
			*/

			//const sanitizer = dompurify.sanitize;
			//text = sanitizer(_.unescape(text));
			return (
				<span
					onClick={(e) => {
						onCLick && onCLick(e);
					}}
					className={`createRichTextElementSpanWrapper ${className ? className : ''}`}
					dangerouslySetInnerHTML={Utility.createMarkup(text)}
				/>
			);
		}
	}

	/**
	 * Given a text with optional placeholders, returns a span element with every placeholder replaced with an anchor tag (<a>) with an onClick handler or not
	 * Example:
	 * text = 'some text blablablba {someKey1} more text ble ble ble {someKey2}' (where {someKey1} and {someKey2} are declared keys in the resource files)
	 * onPlaceholderClickConfig = {
	 * 	someKey1: onClickFunction1,
	 * 	someKey2: onClickFunction2,
	 * }
	 * @param {string} text Can be a plain string, or a string with placeholders that reference another key in the resource file, which can be used to render links with an assigned callback that displays a localized text.
	 * @param {object} onPlaceholderClickConfig Object with one property named after each placeholder key referenced in the text.
	 * @returns {string} placeholder text.
	 * @param {boolean} isNotClickable is not clickable flag
	 * @param {boolean} isDisabled is disabled flag
	 */
	static createClickableTextCommon(text, onPlaceholderClickConfig = {}, isNotClickable, isDisabled) {
		const regex = /(\{[A-Za-z]+\})/;
		if (!text) return null;

		if (onPlaceholderClickConfig && Object.keys(onPlaceholderClickConfig).length > 0) {
			const parsedText = text.split(regex);
			return parsedText.map((segmentText, idx) => {
				if (segmentText.startsWith('{')) {
					const callbackKeyName = segmentText.slice(1, -1);
					const linkCallback = onPlaceholderClickConfig[callbackKeyName];
					if (linkCallback) {
						if (isDisabled) {
							return (
								<MotifButton key={idx} className="createClickableTextLink" variant="text-alt" disabled>
									{labels[callbackKeyName]}
								</MotifButton>
							);
						}
						if (isNotClickable) {
							return (
								<label key={idx} onChange={linkCallback} className="motif-button motif-text-alt-button motif-button-small createClickableTextLink">
									{labels[callbackKeyName]} <input type="file" multiple id="fileInput" name="file" hidden />
								</label>
							);
						}
						return (
							<MotifButton key={idx} className="createClickableTextLink" variant="text-alt" onClick={linkCallback}>
								{labels[callbackKeyName]}
							</MotifButton>
						);
					}
					return Utility.createRichTextElement(labels[callbackKeyName]);
				}
				// Plain text
				return Utility.createRichTextElement(segmentText);
			});
		}

		// Fallback: replace placeholders with bold spans
		let resultText = text;
		let matches;
		do {
			matches = regex.exec(resultText);
			if (matches) {
				const match = matches[0];
				const key = match.substring(1, match.length - 1);
				const replaceText = `<span style='font-weight:bold;'>${labels[key]}</span>`;
				resultText = resultText.replace(match, replaceText);
			}
		} while (matches);
		return Utility.createRichTextElement(resultText);
	}

	/**
	 * Given a text with optional placeholders, returns a span element with every placeholder replaced with an anchor tag (<a>) with an onClick handler if specified in onPlaceholderClickConfig.
	 * Example:
	 * text = 'some text blablablba {someKey1} more text ble ble ble {someKey2}' (where {someKey1} and {someKey2} are declared keys in the resource files)
	 * onPlaceholderClickConfig = {
	 * 	someKey1: onClickFunction1,
	 * 	someKey2: onClickFunction2,
	 * }
	 * @param {string} text Can be a plain string, or a string with placeholders that reference another key in the resource file, which can be used to render links with an assigned callback that displays a localized text.
	 * @param {object} onPlaceholderClickConfig Object with one property named after each placeholder key referenced in the text.
	 * @returns {object} span with placeholder text.
	 * @param {boolean} isNotClickable is not clickable flag
	 * @param {boolean} isDisabled is disabled flag
	 */
	static createClickableText(text, onPlaceholderClickConfig, isNotClickable, isDisabled) {
		return Utility.createClickableTextCommon(text, onPlaceholderClickConfig, isNotClickable, isDisabled);
	}

	/**
	 * Private function to return html required for setting dangerous inner html
	 * @param {string} text - the text to display
	 * @returns {Object} an object with html set to the text passed in
	 * */
	static createMarkup(text) {
		if (typeof text === 'string') {
			const getRidOfImgTags = /&lt;img/g;
			text = text?.replace(getRidOfImgTags, '');
			const sanitizedText = Utility.getSanitizedText(text, {encode: false});
			return {__html: sanitizedText};
		}
	}

	/**
	 * Get a sanitized text after parsing input for XSS.
	 * @param {string} text - the input text
	 * @param {boolean} encode - flag to encode the text, usually for sending over the wire
	 * @returns {string} sanitized text
	 * */
	static getSanitizedText(text, {encode = false}) {
		const sanitizer = DOMPurify.sanitize;
		//let sanitizedText = unescape(sanitizer(text));
		let sanitizedText = sanitizer(text);
		if (encode) {
			sanitizedText = encodeURIComponent(sanitizedText);
		}
		return sanitizedText;
	}

	static getInnerTextFromRichText(html, removeInsertedDeletedData = false) {
		try {
			const element = document.createElement('div');
			element.innerHTML = html;

			if (removeInsertedDeletedData) {
				$(element).find('.diffins').remove();
			}
			// fix special chart from Kendo: &#65279

			// Reading innerText walks the whole subtree, so build the string once.
			const innerText = element.innerText;
			if (innerText) {
				return innerText.replace(/\uFEFF/g, '').trim();
			}
		} catch (err) {
			return '';
		}
	}

	/**
	 * Get a track changes version respone html text after parsing input text of XSS, Rich text etc.
	 * @param {string} text - the input text
	 * @param {boolean} removeInsertedDeletedData - flag to remove diff inserts and update the delete tags to text nodes
	 * @returns {string} html text
	 * */
	static getTrackChangesVersionText(html) {
		try {
			if (Utility.isRichTextContent(html)) {
				const element = document.createElement('div');
				element.innerHTML = html;
				return Utility.createRichTextElement(element.innerHTML.trim(), 'showMoreLessTextResponse', null);
			}
			return Utility.getHtmlTextElement(html.trim());
		} catch (err) {
			return '';
		}
	}

	/**
	 * Check the text content is Rich text or not.
	 * @param {string} text - the input text
	 * @returns {boolean} html text
	 * */
	static isRichTextContent(content) {
		// Regular expression to search for <p> tags
		const paragraphRegex = /<p[^>]*>[\s\S]*?<\/p>/gi;

		// Regular expression to search for track changes diff markup
		// Matches <ins> or <del> tags that have a class containing "diffins" or "diffdel"
		// corresponding to the tag name, e.g. <ins class="diffins ..."> or <del class="diffdel ...">,
		// regardless of attribute order or additional classes.
		const diffMarkupRegex = /<(ins|del)\b(?=[^>]*\bclass=(["'])[^"']*\bdiff\1\b[^"']*\2)[^>]*>/gi;

		// Check if the content contains <p> tags or track changes diff markup
		return paragraphRegex.test(content) || diffMarkupRegex.test(content);
	}

	static getDocumentChangeReasonApiUrl(engagementId, documentId) {
		return `${env.getBaseURL(serviceURL, engagementId)}documents/${documentId}/changereasons/`;
	}

	static parseQueryString(queryStringParameters) {
		const parsed = queryString.parse(queryStringParameters);
		const queryParams = {};

		for (const [propName, propValue] of Object.entries(parsed)) {
			queryParams[propName.toLocaleLowerCase()] = propValue;
		}

		if (queryParams.engagementid) {
			queryParams.engagementid = this.getEngagementIdFromUrl(window);
		}

		return queryParams;
	}

	/**
	 * forms canvas inCountry base url.
	 * @param {string} currentRoute - The current route.
	 * @param {string} document - Doc.
	 * @param {string} engagementInformation - Engagement.
	 * @param {string} inCountryServerBaseUrl - In country server base url.
	 * @param {string} herculesBaseUrl - Hercules base url.
	 * @param {boolean} isMFE - Is memory router mode.
	 * @param {string} customProps - Custom props.
	 * @returns {string} url.
	 * */
	static getCanvasWebUrlForDocument = (currentRoute, document, engagementInformation, inCountryServerBaseUrl, herculesBaseUrl, isMFE, customProps) => {
		const engagementId = engagementInformation.engagementId || engagementInformation.id;
		const inCountryFullUrl = inCountryServerBaseUrl + '/pages/index/index.html' + '?';
		const queryString =
			canvasUrlParams.countryid +
			'=' +
			engagementInformation.countryId +
			'&' +
			canvasUrlParams.engagementid +
			'=' +
			engagementId +
			'&' +
			canvasUrlParams.engagementdesc +
			'=' +
			encodeURIComponent(engagementInformation.engagementDescription !== undefined ? engagementInformation.engagementDescription.substring(0, 50) : '') +
			'&' +
			canvasUrlParams.workspacedesc +
			'=' +
			encodeURIComponent(engagementInformation.workspaceDescription !== undefined ? engagementInformation.workspaceDescription.substring(0, 50) : '') +
			'&' +
			canvasUrlParams.workspaceid +
			'=' +
			engagementInformation.workspaceId +
			'&' +
			canvasUrlParams.engagementversion +
			'=' +
			engagementInformation.engagementVersion +
			'&' +
			canvasUrlParams.returnUrl +
			'=' +
			currentRoute;
		const documentTypeId = document.docTypeId ? document.docTypeId : document.documentTypeId;
		switch (documentTypeId) {
			case documentTypes.profile: {
				return inCountryFullUrl + queryString + '#engagementprofile/' + document.id + '/' + documentTypeId;
			}
			case documentTypes.accountSummary: {
				return `${herculesBaseUrl}${smartScreenRoutes.accountSummary}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.scotSummary: {
				return inCountryFullUrl + queryString + '#scotsummary';
			}
			case documentTypes.itsoSummary: {
				return inCountryFullUrl + queryString + '#itsummary';
			}
			case documentTypes.craSummary: {
				return `${herculesBaseUrl}${smartScreenRoutes.craSummary}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.auditPlan: {
				return `${herculesBaseUrl}${smartScreenRoutes.auditPlan}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.groupAuditStructureSummary: {
				return inCountryFullUrl + queryString + '#groupauditstructuresummary';
			}
			case documentTypes.groupAuditInstructionSummary: {
				return inCountryFullUrl + queryString + '#groupauditinstructionsummary';
			}
			case documentTypes.groupComponentSummaryDocument: {
				return inCountryFullUrl + queryString + '#gacomponentsummary/' + document.id + '/' + documentTypeId;
			}
			case documentTypes.workPlanSummary: {
				return inCountryFullUrl + queryString + '#plan';
			}
			case documentTypes.itEvaluation: {
				return inCountryFullUrl + queryString + '#itevaluation/' + document.id;
			}
			case documentTypes.itEvaluationTemporary: {
				return inCountryFullUrl + queryString + '#itevaluation/' + document.id;
			}
			case documentTypes.canvasFormEvidence:
			case documentTypes.canvasFormTemporary: {
				let baseUrl = new URL(window.location.origin);

				if (isMFE) {
					baseUrl = new URL(env.getKronosURL());
				}

				const url = baseUrl + `canvasform?engagementid=${engagementId}&documentid=${document.id}`;
				return url;
			}
			case documentTypes.independenceFormTemplate: {
				return `${herculesBaseUrl}${smartScreenRoutes.independence}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.accountConclusionClassic: {
				return inCountryFullUrl + queryString + '#accountconclusion/' + document.id + '/' + documentTypeId;
			}
			case documentTypes.archive: {
				return `${herculesBaseUrl}${smartScreenRoutes.archiveForm}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.determinePmTeSad: {
				return inCountryFullUrl + queryString + '#materiality/' + document.id + '/' + documentTypeId;
			}
			case documentTypes.sem: {
				return `${herculesBaseUrl}${smartScreenRoutes.sem}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.fourFortyGl: {
				return `${herculesBaseUrl}${smartScreenRoutes.fourFortyGl}?engagementid=${engagementId}&documentid=${document.id}`;
			}
			case documentTypes.profileV2:
			case documentTypes.accountConclusion:
			case documentTypes.guidedWorkflow:
			case documentTypes.guidedWorkflowTemporary:
			case documentTypes.limitedRiskDocumentation:
			case documentTypes.guidedWorkflowV2:
			case documentTypes.estimates:
			case documentTypes.analytics:
			case documentTypes.multiEntityIndividualProfile:
			case documentTypes.MULTIENTITYSUBPROFILE:
			case documentTypes.accountLevelRiskAssessment:
			case documentTypes.picChecklist:
			case documentTypes.eqrChecklist:
			case documentTypes.groupStructure:
			case documentTypes.groupInstruction:
			case documentTypes.guidedWorkflowV2Temporary:
			case documentTypes.canvasFormSnapshot:
			case documentTypes.alraIndividual:
			case documentTypes.clientSharedHelixAnalytic:
			case documentTypes.groupInvolvementIndividualComponent:
			case documentTypes.itFlowIsa315:
			case documentTypes.scotWalkthroughTemporary:
			case documentTypes.scotWalkthrough:
			case documentTypes.estimateIndividualEstimateTemporary:
			case documentTypes.evidenceDisplayedNoApprovalRequired:
			case documentTypes.guidedWorkflowV2NoSignoffRequired:
			case documentTypes.guidedWorkflowV2NoSignoffRequiredTemporary:
			case documentTypes.craSummaryV2:
			case documentTypes.craSummaryV2Temporary:
			case documentTypes.scotSummaryV2:
			case documentTypes.scotSummaryV2Temporary:
			case documentTypes.estimateWalkthrough:
			case documentTypes.estimateWalkthroughTemporary:
			case documentTypes.itAppPlanningIndividual:
			case documentTypes.itAppPlanningIndividualTemporary:
			case documentTypes.itAppRiskAssessmentIndividual:
			case documentTypes.itAppRiskAssessmentIndividualTemporary:
			case documentTypes.itProcessWalkthroughIndividual:
			case documentTypes.itProcessWalkthroughIndividualTemporary:
			case documentTypes.deficiencyIndividual:
			case documentTypes.deficiencyIndividualTemporary:
			case documentTypes.reviewAndApprovalSummary:
			case documentTypes.reviewAndApprovalSummaryTemporary:
			case documentTypes.groupInvolvementSummary:
			case documentTypes.multiEntityIndividualProfileV2NoSignOffRequired:
			case documentTypes.accountStandardWorkingPaper:
			case documentTypes.controlEvaluateDesign:
			case documentTypes.controlTestControl:
			case documentTypes.itControlEvaluateDesign:
			case documentTypes.itControlEvaluateDesignTemporary:
			case documentTypes.itControlTestControl:
			case documentTypes.itControlTestControlTemporary:
			case documentTypes.itControlUpdateProcedure:
			case documentTypes.itControlUpdateProcedureTemporary:
			case documentTypes.itControlConclusion:
			case documentTypes.itControlConclusionTemporary:
			case documentTypes.itControlOtherSubstantiveProcedure:
			case documentTypes.itControlOtherSubstantiveProcedureTemporary:
			case documentTypes.controlUnderstandConfigurationAndMasterData:
			case documentTypes.itControlITGCStep:
			case documentTypes.controlManagementControlStep:
			case documentTypes.flowchartStepCriticalFlowConfirmationStep:
			case documentTypes.controlSummary:
			case documentTypes.controlSummaryTemporary:
			case documentTypes.auditPlanIndividualAccount:
			case documentTypes.auditPlanIndividualAccountTemporary:
			case documentTypes.auditPlanSummaryV2:
			case documentTypes.auditPlanSummaryV2Temporary:
			case documentTypes.socdSummary:
			case documentTypes.socdSummaryTemporary:
			case documentTypes.flowchartStepCriticalFlowConfirmationStepTemporary:
			case documentTypes.deficiencyIndividualItgcDeficiency:
			case documentTypes.deficiencyIndividualItgcDeficiencyTemporary:
			case documentTypes.itEnvironment:
			case documentTypes.itEnvironmentTemporary:
			case documentTypes.entityIndividualSadEvaluation:
			case documentTypes.sadSummary:
			case documentTypes.sadSummaryTemporary: {
				let baseUrl = new URL(window.location.origin);

				if (isMFE) {
					baseUrl = new URL(env.getKronosURL());
				}

				let url = `${baseUrl}guidedworkflowv2/${document.id}?engagementid=${engagementId}`;

				if (customProps?.stentityid) {
					url += `&stentityid=${customProps.stentityid}`;
				}

				if (customProps?.taskId) {
					url += `&taskId=${customProps.taskId}`;
				}
				return url;
			}
			case documentTypes.smartAutomationEvidence:
			case documentTypes.smartAutomationEvidenceTemporary: {
				const path =
					`?engagementid=${engagementId}&countryId=${engagementInformation.countryId}` +
					`&dataCenterId=${customProps.datacenterid}&requestid=${document.smartEvidenceSourceId}`;

				return this.getAutomationHubUrlFromFeature(path, customProps.features);
			}
			default:
				return '';
		}
	};

	static getEngagementCommentStatusName = (statusId) => {
		switch (statusId) {
			case EngagementCommentStatus.open:
				return 'open';
			case EngagementCommentStatus.cleared:
				return 'cleared';
			case EngagementCommentStatus.closed:
				return 'closed';
		}
	};

	static getCurrentUrl = (location) => {
		let result = location.pathname;

		if (result.startsWith('/')) {
			result = result.substring(1);
		}
		result = result.replaceAll('/', '_');

		return `${result}${encodeURIComponent(location.search)}`;
	};

	static openDocumentForEditing = (payload) => {
		console.log('Opening Document 📃✔✔✔✔✔');

		const _connectionID = DocHubClient.getConnectionId();

		const payloadData = {
			...payload.payloadData,
			connectionId: _connectionID || undefined
		};

		const url = {
			openDocument: true,
			fileId: payloadData.documentId,
			docTypeId: payloadData.docTypeId,
			engagementId: payloadData.engagementId,
			docUrl: env.getDocServiceUrl(),
			resourceUri: env.getURL('Engg_AuthAudience_URL'),
			readonly: payload.actionTag === 'openr',
			includeDeleted: payload.includeDeleted
		};

		if (payloadData.fileVersion) {
			url.fileVersion = payloadData.fileVersion;
		}

		const docHelperUri = env.getDocHelperConfig().docHelperApiUri + 'Document' + '?' + Utility.createQueryString(url);

		if (
			!_connectionID &&
			!isNullOrUndefined(payloadData.engagementStatusId) &&
			payloadData.engagementStatusId !== EngagementStatus.CanvasExternalAccessPortalV2
		) {
			// Through docHelper error if no connection available to DocHelper
			console.log('No Connection to DocHelper Available');
			payload.actions.raiseDochelperError(['0010', 'Connection Failed', payload.labels.documentHelperConnectionIssue]);
			return;
		}

		try {
			payload.actions
				.downloadDocumentWithDocumentHelper(docHelperUri, payloadData, {
					connectionid: payloadData.connectionId
				})
				.then((response) => {
					try {
						if (!(response && response.data)) {
							return;
						}
						let filename = '';
						const url = window.URL.createObjectURL(new Blob([response.data]));
						const link = document.createElement('a');
						link.href = url;

						const disposition = response.headers['content-disposition'];
						if (disposition && disposition.toLowerCase().indexOf('attachment') !== -1) {
							var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
							var matches = filenameRegex.exec(disposition);
							if (matches !== null && matches[1]) {
								try {
									filename = decodeURIComponentSafe(matches[1].replace(/['"]/g, ''));
								} catch (err) {
									filename = matches[1].replace(/['"]/g, '');
								}
							}
						}

						// need to figure out file name on download
						link.setAttribute('download', filename);
						document.body.appendChild(link);
						link.click();
					} catch (err) {
						console.log('Unable to download document from DocService.');
					}
				});
		} catch (e) {
			console.log('No Connection to DocHelper Available');
			payload.actions.raiseDochelperError(['0010', 'Connection Failed', payload.labels.documentHelperConnectionIssue]);
		}
	};

	static openDocument = (
		parentName,
		document,
		downloadDocumentWithDocumentHelper,
		raiseDochelperError = undefined,
		actionTag = actionTags.openReadOnly,
		engagementStatusId
	) => {
		const payload = {
			payloadData: {
				documentHubStatus: 'DOWNLOADSTART',
				parentName: parentName,
				documentId: document.id,
				documentName: `${document.documentName}.${document.fileExt}`,
				docTypeId: document.documentTypeId,
				transactionId: document.id,
				documentType: document.documentTypeId,
				ProgressPercent: 0,
				totalPercentage: 100,
				inProgress: true,
				engagementId: document.engagementId,
				dockey: `_${document.id}`,
				engagementStatusId: engagementStatusId
			},
			actions: {
				downloadDocumentWithDocumentHelper,
				raiseDochelperError
			},
			actionTag,
			labels: {
				documentHelperConnectionIssue: currentResource.labels.documentHelperConnectionIssue
			}
		};

		this.openDocumentForEditing(payload);
	};

	static isPriorPeriodEvidence(document) {
		if (typeof document === 'number') {
			return document === documentTypes.priorYearEvidence || document === documentTypes.priorYearEvidenceDocument;
		} else if (typeof document === 'object' && typeof document.documentTypeId !== 'undefined') {
			return document.documentTypeId === documentTypes.priorYearEvidence || document.documentTypeId === documentTypes.priorYearEvidenceDocument;
		}
	}

	static getUniqueParentName(params) {
		let parentName;

		Object.values(params).forEach((param) => {
			if (!parentName) {
				parentName = '';
			}
			if (!_.isEmpty(param)) {
				if (parentName !== '') {
					parentName += '_';
				}

				parentName += param;
			}
		});

		return parentName;
	}

	static getDocumentExtension(
		documentTypeId,
		fileExt,
		hasComments,
		hasHiddenSheets,
		hasHighlights,
		isConflicted,
		trackChange,
		isReturned,
		isReadOnly,
		isConfidential
	) {
		// ToDo: Need to discuss and change accordingly
		let fileIcon;
		let fileStatus = '-blank-small';
		if (fileExt === undefined) {
			fileExt = '';
		}
		if (fileExtensionMapping) {
			const mappedName = _.find(fileExtensionMapping.mapping, function (items) {
				return items.keys.indexOf(fileExt.toUpperCase()) >= 0;
			});

			if (mappedName) {
				fileIcon = mappedName.mapIconName;
			} else {
				fileIcon = 'unknown';
			}
		}

		// Set File status icon (as per priority)
		if (isConflicted) {
			fileStatus = '-conflicts-small';
		} else if (trackChange) {
			fileStatus = '-tracked-changes-small';
		} else if (hasComments) {
			fileStatus = '-comments-small';
		} else if (hasHiddenSheets) {
			fileStatus = '-hidden-sheets-small';
		} else if (isReturned) {
			fileStatus = '-returned-small';
		} else if (isReadOnly) {
			fileStatus = '-locked-small';
		} else if (isConfidential) {
			fileStatus = '-locked-small';
		} else {
			/* empty */
		}

		return fileIcon + fileStatus;
	}

	/// get parent name to show progress bar only selected tab's document
	static getParentNameForSelectedTab = (parentName) => {
		// return if parentname is undefined
		if (!parentName) return;
		const getParentName = parentName.split('_');

		if (getParentName && getParentName.length > 0 && getParentName[1] !== undefined) {
			return documentWithoutTabLayout.includes(getParentName[1]) ? documentTabLayoutTabs.evidenceName : getParentName[1];
		}

		return parentName;
	};

	static createQueryString(obj) {
		var params, i;

		params = [];
		for (i in obj) {
			if (obj.hasOwnProperty(i)) {
				params.push(i + '=' + encodeURIComponent(obj[i]));
			}
		}

		return params.join('&');
	}

	static isPaperProfile = (docType) => {
		return PaperProfileIDs.includes(docType, 0);
	};

	static isDocumentEligibleToOpenInV1 = (documentTypeId) => {
		return OpenInCanvasDocumentTypeIDs.includes(documentTypeId, 0);
	};

	static isClassicSummaryScreenDocumentType = (documentTypeId) => {
		return ClassicSummaryScreenDocumentTypes.includes(documentTypeId, 0);
	};

	static isDocumentTypeFitV2 = (documentTypeId) => {
		return GuidedWorkflowV2DocumentTypes.includes(documentTypeId, 0);
	};

	static isDocumentTypeKronos = (documentTypeId) => {
		return documentTypeId === documentTypes.canvasFormEvidence || documentTypeId === documentTypes.canvasFormTemporary;
	};

	static isDocumentTypeFit = (documentTypeId) => {
		const fitDocumentTypes = [...GuidedWorkflowV2DocumentTypes, documentTypes.guidedWorkflow];

		return fitDocumentTypes.includes(documentTypeId, 0);
	};

	static isDocumentIndependenceOrProfileForm = (documentTypeId) => {
		const independenceOrProfileDocumentTypes = [documentTypes.profileV2, documentTypes.independenceFormIndividual, documentTypes.independenceFormTemplate];

		let isIndependenceOrProfile;
		if (!isNullOrUndefined(documentTypeId)) {
			isIndependenceOrProfile = independenceOrProfileDocumentTypes.includes(documentTypeId, 0);
		}

		return isIndependenceOrProfile;
	};

	static GetDocHelperStateKey(documentList, keyToSearch) {
		const keys = [];
		//Get all keys which mach with given key passed so that we can update the state
		_.map(documentList, function (value, key) {
			if (key.indexOf(keyToSearch) === 0) {
				keys.push(key);
			}
		});

		return keys;
	}

	/**
	 * Returns a boolean that determines if a change reason is required after uploading / editing a document.
	 * @param {object} engagement - Engagement object from the store.
	 * @param {object} quickLink - QuickLink object from the store.
	 * @param {object} document - Document data.
	 * @returns {bool} - Returns a boolean that determines if a change reason is required after uploading / editing a document
	 * */
	static isChangeReasonRequired(engagement, quickLink, document) {
		let changeReasonRequired = false;

		if (engagement?.engagementStatusId !== EngagementStatus.Restored) {
			let currentDate = moment().utc(),
				engagementReportDate;

			// A change reason is required is the current date is after the report release date and the engagement has a 440GL document
			// and the document does not have a conflict.
			if (engagement && engagement.expectedReportReleaseDate) {
				engagementReportDate = moment.utc(engagement.expectedReportReleaseDate);
				const isConflicted = document && document.isConflicted;

				changeReasonRequired = !isConflicted && quickLink && quickLink.hasModificationsAfterRRD && currentDate.isAfter(engagementReportDate);
			}
		}

		return changeReasonRequired;
	}

	static getTaskDocumentsApiUrl(inputData) {
		let url = env.getBaseURL(serviceURL, inputData.engagementId) + 'Tasks/' + inputData.taskId + '/Documents?ReturnOnlyRelatedDocuments=true';

		if (inputData.workspaceId === undefined) {
			inputData.workspaceId = 0;
		}
		if (inputData.taskTypeId === undefined) {
			inputData.taskTypeId = 0;
		}

		if (inputData.queryStringInsert) {
			url =
				url +
				'&' +
				'workspaceid=' +
				inputData.workspaceId +
				'&' +
				'taskTypeId=' +
				inputData.taskTypeId +
				(inputData.returnTaskStatusAndFlagsOnly === true ? '&' + 'returnTaskStatusAndFlagsOnly=' + inputData.returnTaskStatusAndFlagsOnly : '') +
				(inputData.isV2Return === true ? '&' + 'isV2Return=' + inputData.isV2Return : '');
		}
		return url;
	}

	static getAnnotationUrlByCategory(categoryId, annotationId, entityId) {
		let result;
		if (entityId) {
			result = (categoryId === entities.Document ? '&documentId=' : '&taskId=') + entityId;
		} else {
			result = '&annotationId=' + annotationId;
		}
		return result;
	}

	static getAnnotationsUrl(
		annotationId,
		entityId,
		engagementId,
		categoryId,
		returnsOnlyCount,
		includeAnnotationList,
		includeAnnotationReplies,
		annotationStatusIds,
		assignedTo,
		authoredBy,
		search,
		orderBy,
		pageSize,
		showAuthorAndAssigned,
		engagementSource = 1
	) {
		let optionalParamsQuery = '';
		if (search) {
			optionalParamsQuery += '&searchString=' + encodeURIComponent(search);
		}
		if (orderBy) {
			optionalParamsQuery += '&sortOrder=' + orderBy;
		}
		if (pageSize) {
			optionalParamsQuery += '&pageSize=' + pageSize;
		}
		if (assignedTo && !_.isEmpty(assignedTo)) {
			optionalParamsQuery += assignedTo.map((item) => `&reviewNotesAssignedTo=${item}`).join('');
		}
		if (authoredBy && !_.isEmpty(authoredBy)) {
			optionalParamsQuery += authoredBy.map((item) => `&reviewNotesAuthoredBy=${item}`).join('');
		}
		if (annotationStatusIds && !_.isEmpty(annotationStatusIds)) {
			optionalParamsQuery += annotationStatusIds.map((item) => `&annotationStatusIds=${item}`).join('');
		}
		if (showAuthorAndAssigned) {
			optionalParamsQuery += '&showAuthorAndAssigned=' + showAuthorAndAssigned;
		}
		return (
			env.getBaseURL(serviceURL, engagementId) +
			'Annotations?categoryId=' +
			categoryId +
			this.getAnnotationUrlByCategory(categoryId, annotationId, entityId) +
			'&returnsOnlyCount=' +
			returnsOnlyCount +
			'&includeAnnotationList=' +
			includeAnnotationList +
			'&includeAnnotationReplies=' +
			includeAnnotationReplies +
			'&engagementSource=' +
			engagementSource +
			optionalParamsQuery
		);
	}

	static getLocale(userPreferences) {
		let result = '';
		if (userPreferences) {
			result = userPreferences.displayLanguage;
			switch (userPreferences.languageId) {
				case 8:
					result = 'ja';
					break;
				case 9:
					result = 'zh-cn';
					break;
				case 10:
					result = 'zh-hk';
					break;
				default:
					break;
			}
		}
		return result;
	}

	static getTotalAccountCount = (accounts) => {
		return (
			Utility.getAccountTypeCount(accounts, AccountType.Significant) +
			Utility.getAccountTypeCount(accounts, AccountType.LimitedRisk) +
			Utility.getAccountTypeCount(accounts, AccountType.InSignificant)
		);
	};

	static getAccountTypeCount = (accounts, accountTypeId) => {
		if (accounts) {
			return accounts.filter((item) => item.accountTypeId === accountTypeId).length;
		}

		return 0;
	};

	static getAccountSeries = (option, significantCount, limitedCount, insignificantCount) => {
		const series = [];
		option = option.toString();
		const {AccountSummaryAccountType} = currentResource;
		AccountSummaryAccountType.slice(0)
			.reverse()
			.map((account) => {
				switch (account.id) {
					case '1':
						if (significantCount > 0) {
							series.push({
								name: labels.significantAccountsBarChart + ' (' + significantCount + ')',
								data: [significantCount],
								color: option === '0' || option === '1' ? 'rgba(21, 92, 180, 1)' : 'rgba(21, 92, 180, .3)',
								inactive: true
							});
						}
						break;
					case '2':
						if (limitedCount > 0) {
							series.push({
								name: labels.limitedAccounts + ' (' + limitedCount + ')',
								data: [limitedCount],
								color: option === '0' || option === '2' ? 'rgba(24, 140, 229, 1)' : 'rgba(24, 140, 229, .3)'
							});
						}
						break;
					case '3':
						if (insignificantCount > 0) {
							series.push({
								name: labels.insignificantAccounts + ' (' + insignificantCount + ')',
								data: [insignificantCount],
								color: option === '0' || option === '3' ? 'rgba(135, 211, 242, 1)' : 'rgba(135, 211, 242, .3)'
							});
						}
						break;
				}
			});

		return series;
	};

	static getValueInUserPrefferedNumberFormat = (value, numberFormat, isNumberFormattingRequired = true) => {
		if (value !== null) {
			if (value < 1 && value > 0) {
				// if this is between these 2 then treat it as '1'
				value = 1;
			}

			value = Math.round(value); // round it to integer

			const newValue = isNumberFormattingRequired ? numberFormattedText(numberFormat, value === null || value === undefined ? '' : value.toString()) : value;

			return newValue;
		}

		return '';
	};

	/**
	 * Return the current date format based in user preferences.
	 * @param {object} userPreferences - userPreferences from store
	 * @returns {string} current date format.
	 * */
	static getUserPreferencesDateLabel(userPreferences) {
		let dateFormat = dateFormats[defaultDateFormat] && dateFormats[defaultDateFormat].label;
		if (userPreferences.isPreferencesLoaded) {
			dateFormat = dateFormats[userPreferences.dateFormat] && dateFormats[userPreferences.dateFormat].label;
		}
		return dateFormat;
	}

	/**
	 * Return the current date format based in user preferences.
	 * @param {object} userPreferences - userPreferences from store
	 * @param {bool} isLocale - isLocale. Defaults to false.
	 * @returns {string} current date format.
	 * */
	static getUserPreferencesDateValue(userPreferences, isLocale = false) {
		let dateFormat = isLocale
			? dateFormats[defaultDateFormat] && dateFormats[defaultDateFormat].value
			: dateFormats[defaultDateFormat] && dateFormats[defaultDateFormat].momentLocale;
		if (userPreferences?.isPreferencesLoaded) {
			dateFormat =
				dateFormats[userPreferences?.dateFormat] &&
				(isLocale ? dateFormats[userPreferences?.dateFormat].value : dateFormats[userPreferences?.dateFormat].momentLocale);
		}
		return dateFormat;
	}

	static getFormattedDateValue(userPreferences, date) {
		if (userPreferences && date) {
			const userDateFormat = Utility.getUserPreferencesDateValue(userPreferences);
			const formatedDate = moment(date);
			return Utility.getFormattedDate(formatedDate, userDateFormat);
		}

		return '';
	}

	static getEngagementUsersUrl(engagementId, userStatus) {
		let optionalParams = '';

		if (userStatus && !_.isEmpty(userStatus)) {
			optionalParams += userStatus.map((item) => `&userStatus=${item}`).join('');
		}

		let returnValue = env.getBaseURL(serviceURL, engagementId) + 'Users';

		if (optionalParams) {
			returnValue += '?' + optionalParams.substring(1); //removing first & to make adding params easier
		}

		return returnValue;
	}

	static getDocumentHelperKey = (documentId, documentName, fileExt) => {
		let result = `${documentName}${fileExt ? `.${fileExt}` : ''}`;
		if (documentId) {
			result += `_${documentId}`;
		}

		return result;
	};

	static normalize = (field, accumulator, key, replacePush) => {
		let result = accumulator;

		// If an accumulator is passed, the result should be at least, that
		if (!result) {
			result = {
				byId: {},
				allIds: []
			};
		}

		// Run array normalization logic
		if (field) {
			if (Array.isArray(field)) {
				for (let k = 0; k < field.length; k++) {
					const entity = field[k];

					const index = key ? entity[key] : entity.id;
					const resultEntity = result.byId[index];

					if (!replacePush || !resultEntity) {
						result.allIds.push(index);
					}
					result.byId[index] = {...resultEntity, ...entity};
				}
			} else {
				const index = key ? field[key] : field.id;
				if (!replacePush || !result.byId[index]) {
					result.allIds.push(index);
				}
				result.byId[index] = {...result.byId[index], ...field};
			}
		}

		return result;
	};

	static safeNormalize = (field, accumulator, key = 'id', replacePush) => {
		let newObject;
		if (accumulator) {
			newObject = {
				...accumulator,
				byId: {...accumulator.byId},
				allIds: [...accumulator.allIds]
			};
		}

		return this.normalize(field, newObject, key, replacePush);
	};

	static getUrlForFormData = (engagementId, documentId, queryParams) => {
		return env.getBaseURL(serviceURL, engagementId) + 'Forms/' + documentId + '?' + queryParams;
	};

	static validateDocumentName(documentName, isPriorPeriod) {
		let error = false;
		let errorMessage = '';

		const regexEmpty = /^\s*$/;
		const regexCharacter = /^[^"<*/:\\?|>]+$/i;

		if (regexEmpty.test(documentName)) {
			if (errorMessage !== '') {
				errorMessage += ' ';
			}

			errorMessage += isPriorPeriod ? labels.docNameRequired : labels.fileNameRequired;
		} else if (!documentName.match(regexCharacter)) {
			if (errorMessage !== '') {
				errorMessage += ' ';
			}

			errorMessage += isPriorPeriod ? labels.docInvalidCharacters : labels.invalidCharacters;
		}

		if (documentName.length > documentNameMaxLength) {
			if (errorMessage !== '') {
				errorMessage += ' ';
			}

			errorMessage += labels.maxLengthExceeded;
		}

		if (errorMessage !== '') {
			error = true;
		} else {
			errorMessage = undefined;
		}

		return [error, errorMessage];
	}

	static validateInputText(inputText, errorLabel) {
		let error = false;
		let errorMessage = '';

		const regexCharacter = /^[^"<*/:\\?|>]+$/i;

		if (!inputText.match(regexCharacter)) {
			errorMessage = errorLabel;
		}

		if (errorMessage !== '') {
			error = true;
		} else {
			errorMessage = undefined;
		}

		return [error, errorMessage];
	}

	// Returns whether Body Is Disabled
	static IsBodyDisabled = (permissions, actions) => {
		let isBodyDisabled = false;

		if (!permissions.canUpdateEngagementContent) {
			isBodyDisabled = false;
		} else if (permissions.canUpdateEngagementContent) {
			const actionFound = actions.some((action) => {
				return action.actionTypeId === actionType.IndependenceException;
			});

			isBodyDisabled = actionFound;
		}

		return isBodyDisabled;
	};

	static getFormattedDate = (date, dateFormat) => {
		if (date instanceof moment) {
			return date.format(dateFormat);
		}
		return date;
	};

	static getLocalizedFormattedDateTime = (date) => {
		return date.replace('AM', labels.amLabel).replace('PM', labels.pmLabel);
	};

	/*********************************
					TASK
	***********************************/

	static getTaskUrl = (
		taskId,
		taskTypeId,
		engagementInformation,
		versionNumber = null,
		isV1Redirect = null,
		categoryId = OfflineTaskListFilters.categoryId,
		subCategoryId = OfflineTaskListFilters.subcategoryId,
		documentId
	) => {
		let url = '';
		let redirectToCanvas = false;
		if (versionNumber) {
			redirectToCanvas = versionNumber === 1;
		} else {
			redirectToCanvas = isV1Redirect || false;
		}
		if (redirectToCanvas) {
			url = this.getCanvasTaskDetailsWebUrl(canvasUrlParams.taskDetails, engagementInformation, taskId, taskTypeId);
		} else {
			/*url = `${env.getURL('hercules_web')}/dashboard?category=all
		&deeplinkdocumentid=${documentId}&engagementid=${engagementInformation.engagementId}&searchtype=contains
		&deeplinktaskid=${taskId}&viewdocumenttab=evidences&viewtype=tasks&taskdrawer=true#engagementOverview`;*/
			url = env.getWebUrl(`${taskApiRoutes.task}${categoryId}/${subCategoryId}/${taskId.toLowerCase()}?engagementid=${engagementInformation.engagementId}`);
		}
		return url;
	};

	/**
	 * forms canvas inCountry url.
	 * @param {string} route - the route to add to canvas url
	 * @param {string} engagementInformation - engagement information
	 * @param {string} taskId - current route values. State values
	 * @param {number} taskTypeId - task type id
	 * @returns {string} canvas inCountry url for the route
	 * */
	static getCanvasTaskDetailsWebUrl(route, engagementInformation, taskId, taskTypeId) {
		const inCountryBaseUrl = env.getURL('eng_web');
		const engagementId = engagementInformation.engagementId || engagementInformation.id;

		return [
			inCountryBaseUrl,
			'/pages/index/index.html',
			'?',
			canvasUrlParams.countryid,
			'=',
			engagementInformation.countryId,
			'&',
			canvasUrlParams.engagementid,
			'=',
			engagementId,
			'&',
			canvasUrlParams.engagementdesc,
			'=',
			encodeURIComponent(engagementInformation.engagementDescription?.substring(0, 50)),
			'&',
			canvasUrlParams.workspacedesc,
			'=',
			encodeURIComponent(engagementInformation.workspaceDescription?.substring(0, 50)),
			'&',
			canvasUrlParams.workspaceid,
			'=',
			engagementInformation.workspaceId,
			'&',
			canvasUrlParams.engagementversion,
			'=',
			engagementInformation.engagementVersion,
			'&',
			canvasWebRoutes[route],
			'/',
			taskId,
			'/',
			taskTypeId
		].join('');
	}

	static getTaskTypeDetails(taskTypeId, isEp, isEq, isEpEqr) {
		let taskType = -1;
		let isUserCreated = false;

		if (_.inRange(Number(taskTypeId), 1, 7)) {
			/*Control task*/
			taskType = 4;
			isUserCreated = true;
		} else if (_.inRange(taskTypeId, 7, 12)) {
			/*Response Task*/
			taskType = 7;
			isUserCreated = false;
		} else if (_.inRange(taskTypeId, 12, 14)) {
			/*Other Standard Task*/
			taskType = 1;
			isUserCreated = true;
		} else if (taskTypeId === 14) {
			/*Primary Standard Task*/
			taskType = 2;
			isUserCreated = false;
		} else if (taskTypeId === 22) {
			/*Account Conclusion Task*/
			taskType = 22;
			isUserCreated = false;
		} else if (taskTypeId === 15 || taskTypeId === 16 || taskTypeId === 26) {
			/*Substantive Task*/
			taskType = 5;
			isUserCreated = true;
		} else if (taskTypeId === 17) {
			/*Substantive Task*/
			taskType = 6;
			isUserCreated = true;
		} else if (taskTypeId === 20) {
			/*SCOT Task*/
			taskType = 20;
			isUserCreated = true;
		} else if (taskTypeId === 28) {
			/*ESTIMATES Task*/
			taskType = 28;
			isUserCreated = true;
		} else if (_.inRange(taskTypeId, 18, 21)) {
			/*Walkthrough Task*/
			taskType = taskTypeId === 19 ? 23 : 3;
			isUserCreated = true;
		} else if (taskTypeId === 29) {
			/*EDC Task*/
			taskType = 29;
			isUserCreated = true;
		} else if (_.inRange(taskTypeId, 23, 26)) {
			/*Group Task*/
			taskType = 8;
			isUserCreated = false;
		} else if (taskTypeId === 27) {
			/*Group Task*/
			taskType = 8;
			isUserCreated = true;
		} else {
			/*show blank*/
			taskType = 0;
		}

		if (isEp && isEp === true) {
			taskType = 9;
			isUserCreated = false;
		}
		if (isEq && isEq === true) {
			taskType = 10;
			isUserCreated = false;
		}
		if ((isEpEqr && isEpEqr === true) || (isEp && isEp === true && isEq && isEq === true)) {
			taskType = 11;
			isUserCreated = false;
		}

		return {
			taskType: taskType,
			isUserCreated: isUserCreated,
			description: taskType >= 0 ? currentResource.taskTypeBadge[taskType] : ''
		};
	}
	// This is used to hide/show buttons in modal
	static displayButtons = (isOverwriteModal) => {
		return isOverwriteModal ? true : false;
	};

	// Return title to the modal
	static getModalTitle = (comingFrom, isOverwriteModal, categoryName) => {
		if (isOverwriteModal) return labels.overwriteForm;

		switch (comingFrom) {
			case relateDocumentsTo.canvasForm:
				return labels.relateDocumentsTitle;
			case relateDocumentsTo.canvasFormBody:
				return labels.relateDocumentsToBodyTitle;
			case relateDocumentsTo.canvasFormBodyAres:
				switch (categoryName) {
					case documentCategoryNames.priorPeriod:
						return labels.priorPeriodRelateDocument;
					case documentCategoryNames.temporary:
						return labels.relateTemporaryFiles;
					case documentCategoryNames.external:
						return labels.relateExternalDocuments;
					default:
						return labels.relateDocumentsToBodyAresTitle;
				}
			default:
				return labels.relateDocumentsTitle;
		}
	};

	// Return snapshot operation
	static getSnapshotCompareLabel = (snapshotModel, isTrackChanges) => {
		if (snapshotModel?.isAdded) {
			return isTrackChanges ? labels.added : labels.snapshotNew;
		}
		if (snapshotModel?.isChanged) {
			return labels.snapshotUpdate;
		}
		if (snapshotModel?.isDeleted && !isTrackChanges) {
			return labels.snapshotRemoved;
		}
		return undefined;
	};

	static formatSnapshotsName = (snapshots) => {
		let snapShotsFromServer = [];
		if (snapshots && snapshots.length > 0) {
			snapShotsFromServer = snapshots.map((item) => {
				let snapshotName;

				switch (item.documentSourceID) {
					case documentSources.RESTORE:
						snapshotName = labels.snapshotRestore;
						break;
					case documentSources.ROLLFORWARD:
						snapshotName = labels.snapshotRollforward;
						break;
					case documentSources.COPYENGAGEMENT:
					case documentSources.COPYCANVASFORM:
						snapshotName = labels.snapshotCopy;
						break;
					default:
						snapshotName = item.userName;
				}

				return {
					value: item.id,
					label: item.userName,
					title: item.userName,
					userName: snapshotName,
					createDate: item.createDate
				};
			}, this);
		}
		return snapShotsFromServer;
	};

	static getInstructionalText = (comingFrom, isOverwriteModal, categoryName) => {
		if (isOverwriteModal) return labels.overwriteFormDesc;

		switch (comingFrom) {
			case relateDocumentsTo.canvasForm:
				return labels.relateDocumentsDesc;
			case relateDocumentsTo.canvasFormBody:
			case relateDocumentsTo.canvasFormBodyAres:
				return categoryName === documentCategoryNames.priorPeriod ||
					categoryName === documentCategoryNames.external ||
					categoryName === documentCategoryNames.temporary
					? labels.relateDocumentsToTheBody
					: labels.relateDocumentsToBodyDesc;
			default:
				return labels.relateDocumentsDesc;
		}
	};

	// Return title for ADC modal
	static getAdcModalTitle = (entityId, isSharedForm) => {
		switch (entityId) {
			case entities.SCOT:
				return isSharedForm ? labels.viewScot : labels.editScot;
			case entities.WCGW:
				return isSharedForm ? labels.viewWcgw : labels.editWcgw;
			case entities.ItProcess:
				return isSharedForm ? labels.viewITSOProcess : labels.editITSOProcess;
			case entities.ItSoApplication:
				return isSharedForm ? labels.viewITAppSO : labels.editITAppSO;
			case entities.Control:
				return isSharedForm ? labels.viewControl : labels.editControl;
			case entities.ItControl:
				return isSharedForm ? labels.viewITControl : labels.editITControl;
			case entities.Risk:
				return isSharedForm ? labels.viewRisk : labels.editRisk;
		}
	};

	static getAdcStrategyTitle = (entityId) => {
		switch (entityId) {
			case entities.SCOT:
				return labels.scotStrategy;
			case entities.WCGW:
				return labels.wcgwStrategy;
			case entities.ItProcess:
				return labels.itProcessStrategy;
		}
	};

	// Return description for ADC modal
	static getAdcModalDescriptionTitle = () => {
		return Utility.createRichTextElement(labels.editCanvasformObjects, '');
	};

	// Return description for View modal
	static getViewModalDescription = (entityName, className) => {
		const name = <span className={`${className ? className : ''}`}>{entityName}</span>;
		return (
			<span>
				{Utility.createRichTextElement(labels.scotRelatedControls)}
				{name}
			</span>
		);
	};

	// get class to DisableADCLink
	static getClasstoDisableADCLink(userPreferences, isSnapshotView) {
		if ((userPreferences && userPreferences.isGuestUser && !userPreferences.isCEAPUser) || isSnapshotView) {
			return 'disableLinksExternalUsers';
		}
		return '';
	}

	/**
	 * Return text, text wrapped in a span or rich text.
	 * @param {string} text - text to return
	 * @param {boolean} isSnapshotView - if snapshot just return text.
	 * @param {boolean} isRichText - most inputs do not allow for rich text, pass false if not needing to render rich text.
	 * @returns {object} - returns a span with text in it.
	 */
	static getHtmlTextElement(text, isSnapshotView, isRichText = true) {
		if (!isSnapshotView) {
			return text;
		}
		if (!isRichText) {
			return <span className="RichTextBodySpan NoRichText">{text}</span>;
		}
		return <span className="RichTextBodySpan" dangerouslySetInnerHTML={Utility.createMarkup(text)} />;
	}

	static getHtmlTextWithTitleTip(text, className) {
		return <span title={text} className={className} dangerouslySetInnerHTML={Utility.createMarkup(text)} />;
	}

	static removeStoredPayload = (bodyId) => {
		Utility.clearStorage(bodyId);
	};

	static getDragAndDropUploadObject = () => {
		return dragAndDropUploadObject;
	};

	static setDragAndDropUploadObject = (object) => {
		dragAndDropUploadObject.push(object);
	};

	static removeDragAndDropUploadObject = () => {
		dragAndDropUploadObject = [];
	};

	static hasDragAndDropUploadObject = () => {
		return dragAndDropUploadObject.length > 0;
	};

	static getSignoffsRequirements = (bodyTypeId) => {
		switch (bodyTypeId) {
			case formBodyType.DocumentUploadEQRApprovalRequired:
				return {
					isEQRSignoffUserRequired: true
				};
			case formBodyType.DocumentUploadPICApprovalRequired:
				return {
					isEPSignoffUserRequired: true
				};
			case formBodyType.DocumentUploadPICEQRApprovalRequired:
				return {
					isEQRSignoffUserRequired: true,
					isEPSignoffUserRequired: true
				};
			case formBodyType.DocumentUploadAnyApprovalRequired:
				return {
					isEQRSignoffUserRequired: false,
					isEPSignoffUserRequired: false
				};
			default:
				return {};
		}
	};

	static getAresTaskListNavigationUrl = (
		documentId,
		headerId,
		sectionId,
		engagementId,
		taskId,
		queryParameters,
		documentTypeId,
		isTrackChanges,
		isCompareResponses,
		isAttributesView,
		stEntityId,
		isRelatedDocuments
	) => {
		const excludeKeys = [];
		if (engagementId) {
			excludeKeys.push('engagementid');
		}

		if (taskId) {
			excludeKeys.push('taskid');
		}

		if (documentId) {
			excludeKeys.push('documentid');
		}

		const _headerId = headerId === -1 ? 'summary' : headerId;

		// As we have Track changes, Compare responses, Attributes, isRelatedDocuments view only on v2. Default to v2
		let url = `/${aresTaskViewRoutes.guidedworkflowV2}`;

		if (this.isDocumentTypeKronos(documentTypeId)) {
			url = '/canvasform';
			url += `?${canvasUrlParams.engagementid}=${engagementId}`;
			url += `&${canvasUrlParams.documentId}=${documentId}`;
			const query = getFilteredQueryParameter(queryParameters, excludeKeys);
			if (query) {
				url += this.appendObjectParams(query);
			}
			return url;
		}

		if (isTrackChanges) {
			url += `/changes`;
		} else if (isCompareResponses) {
			url += `/compare`;
		} else if (isAttributesView) {
			url += `/attributes`;
		} else if (isRelatedDocuments) {
			url += `/relateddocuments`;
		}

		if (documentId) {
			// Activity page url:
			url += `/${documentId}`;

			if (_headerId && _headerId !== 'summary') {
				url += `/${aresTaskViewRoutes.header}/${_headerId}`;

				if (sectionId) {
					url += `/${aresTaskViewRoutes.section}/${sectionId}`;
				}
			} else if (_headerId === 'summary') {
				url += `/${aresTaskViewRoutes.summary}`;
			}
		}

		url += `?${canvasUrlParams.engagementid}=${engagementId}`;

		if (taskId) {
			url += `&taskId=${taskId}`;
		}

		if (stEntityId) {
			delete queryParameters.stEntityId;
			delete queryParameters.stentityid;
			url += `&stentityid=${stEntityId}`;
		}
		const query = getFilteredQueryParameter(queryParameters, excludeKeys);
		if (query) {
			url += this.appendObjectParams(query);
		}

		return url;
	};

	static appendObjectParams = (params) => {
		let append = '';

		Object.entries(params).forEach((entry) => {
			const [name, value] = entry;
			append += `&${name.toLowerCase()}`;
			if (value !== null && value !== undefined) {
				append += `=${value}`;
			}
		});

		return append;
	};

	static getAccountParams = (queryParameters) => {
		let params = '';

		if (queryParameters) {
			Object.entries(queryParameters).forEach((entry, i) => {
				const [name, value] = entry;
				params += i === 0 ? `${name}` : `&${name}`;
				if (value !== null && value !== undefined) {
					params += `=${value}`;
				}
			});
		}

		return params;
	};

	static convertUserPreferencesDateFormatToMoment(userFormat) {
		switch (userFormat) {
			case dateFormats[0].id:
				return 'MM/DD/YYYY';
			case dateFormats[1].id:
				return 'DD/MM/YYYY';
			case dateFormats[2].id:
				return 'YYYY/MM/DD';
		}
	}

	static formatGetAllDocumentsApprovalsForSignOffsControl(approvals, userPreferences) {
		return approvals?.map((approval) => {
			return {
				...approval,
				approvalId: approval.id,
				isFromLoggedInUser: userPreferences.id === approval.approvalUserId
			};
		});
	}

	static getTheme() {
		return document.getElementsByTagName('body')[0].getAttribute('motif-theme') === 'light' ? null : 'dark';
	}

	static isDarkTheme = () => document.getElementsByTagName('body')[0].getAttribute('motif-theme') === 'dark';

	static getRouteValueAt = (idx) => {
		const routeTokens = location.pathname.replace(/^\/+/g, '').split('/');
		return routeTokens.length > idx ? routeTokens[idx] : undefined;
	};

	static showISA315Counter = (counter) => {
		return counter > 0 ? counter : '-';
	};

	static getInitials = (names) => {
		const _names = names || '';
		let initials = '';

		if (_names.length === 1) {
			initials = names[0];
		} else {
			initials = _names
				.split(' ')
				.map((item) => item.charAt(0))
				.join('');
		}

		return initials.substring(0, 3).toUpperCase();
	};

	static isValueModified(originalValue, modifiedValue) {
		if (modifiedValue !== undefined && modifiedValue !== null && modifiedValue !== '' && modifiedValue !== originalValue) {
			return true;
		}

		return false;
	}

	static hasValue(value) {
		let result = false;

		switch (typeof value) {
			case 'object':
				if (Array.isArray(value)) {
					if (value.length > 0) {
						result = true;
					}
				} else {
					result = !_.isEmpty(value);
				}
				break;
			case 'string':
				if (value !== undefined && value !== null && value.trim() !== '') {
					result = true;
				}
				break;
			default:
				if (value !== undefined && value !== null) {
					result = true;
				}
				break;
		}

		return result;
	}

	static calculateValidationCounters(headers, validations, filter, documentTypeId) {
		let incompleteCount = 0,
			unresolvedCommentCount = 0;

		headers &&
			headers.length > 0 &&
			validations &&
			validations.length > 0 &&
			headers.forEach((header) => {
				const headerCounts = getValidationCountForIds(header, undefined, undefined, validations, filter, documentTypeId);

				if (filter === validationFilter.INCOMPLETE || filter === validationFilter.NONE) {
					incompleteCount += headerCounts.incompleteCount;
				}

				if (filter === validationFilter.COMMENTS || filter === validationFilter.NONE) {
					unresolvedCommentCount += headerCounts.unresolvedCount;
				}
			});

		return {incompleteCount, unresolvedCommentCount};
	}

	static calculateValidationForSingleHeader(headerId, validations, filter, documentTypeId) {
		let incompleteCount = 0,
			unresolvedCommentCount = 0;

		if (validations && validations.length > 0) {
			const headerCounts = getValidationCountForIds(headerId, undefined, undefined, validations, filter, documentTypeId);

			if (filter === validationFilter.INCOMPLETE || filter === validationFilter.NONE) {
				incompleteCount += headerCounts.incompleteCount;
			}

			if (filter === validationFilter.COMMENTS || filter === validationFilter.NONE) {
				unresolvedCommentCount += headerCounts.unresolvedCount;
			}
		}
		return {incompleteCount, unresolvedCommentCount};
	}

	static calculateValidationForSingleSection(headerId, sectionId, validations, filter, documentTypeId) {
		let incompleteCount = 0,
			unresolvedCommentCount = 0;

		if (validations && validations.length > 0) {
			const sectionCounts = getValidationCountForIds(headerId, sectionId, undefined, validations, filter, documentTypeId);

			if (filter === validationFilter.INCOMPLETE || filter === validationFilter.NONE) {
				incompleteCount += sectionCounts.incompleteCount;
			}

			if (filter === validationFilter.COMMENTS || filter === validationFilter.NONE) {
				unresolvedCommentCount += sectionCounts.unresolvedCount;
			}
		}
		return {incompleteCount, unresolvedCommentCount};
	}

	static getDateFormatWithoutYear = (dateFormat) => {
		let result = dateFormat;
		const regExp = '(^/)*([/yY]{5})|($/)w+';
		const capture = result.match(regExp);
		if (capture !== null && capture.length > 0) {
			result = result.replace(capture[0], '');
		}
		return result;
	};

	static addDays = (date, days) => {
		const returnDate = new Date(date);
		returnDate.setDate(returnDate.getDate() + days);
		return returnDate;
	};

	static applyRounding = (value, decimals = 0) => {
		return parseFloat(applyRound(value, decimals));
	};

	static mapOrder = (array, order, key) => {
		array.sort(function (a, b) {
			var A = a[key],
				B = b[key];

			if (order.indexOf(A) > order.indexOf(B)) {
				return 1;
			} else {
				return -1;
			}
		});

		return array;
	};

	static isSmartAutomationEvidence = (documentTypeId) => {
		return SmartAutomationEvidenceIDs.includes(documentTypeId, 0);
	};

	static isVoiceNoteRecordingEnabled = (state) => {
		// This value is from server configuration. It would applies for all engagements on that server.
		const serverIsVoiceNoteRecordingEnabled = !!state.configurations?.features?.isVoiceNoteRecordingEnabled;

		// This value only applies when one of the following occurs:
		// - US GAAS + Listed
		// - Canadian GAAS + Listed
		// - Canadian GAAS + Other public interest entity that is not listed
		const engagementIsVoiceNoteRecordingEnabled = !!state.engagement?.isVoiceNoteRecordingEnabled;

		return serverIsVoiceNoteRecordingEnabled && engagementIsVoiceNoteRecordingEnabled;
	};

	static getOARHelixLink = (helixData, trialBalance, type) => {
		let url = '';
		if (helixData && helixData?.length > 0 && trialBalance && trialBalance?.connections) {
			const oarData = trialBalance?.trialBalanceAttributeJson;
			const primaryAnalyzer = helixData.find(
				(project) => project.projectId === trialBalance.connections.projectId && project.helixImportStatusID === helixImportStatus.importCompleted
			);
			if (primaryAnalyzer) {
				url = env.getBaseURL('Helix_Platform_Base_Url') + 'app/projects/view-analytics/';
				url += `${primaryAnalyzer?.projectId}/${primaryAnalyzer?.helixAnalyzerId}`;
				let businessUnit = '';
				if (oarData?.businessUnit && oarData?.businessUnit.length > 0) {
					businessUnit += '~&~BU=';
					oarData?.businessUnit.forEach((bu) => {
						businessUnit += bu + '~|~';
					});
				}
				if (businessUnit.length > 0 && businessUnit.charAt(businessUnit.length - 1) === '~') {
					businessUnit = businessUnit.substring(0, businessUnit.length - 3);
				}
				let queryString = '';
				if (oarData?.secondComparativePeriod) {
					queryString = `Report_Key=FSC~&~Component_key=Balcomp3p~&~AnalysYtd=${oarData?.analysisPeriod?.yearToDateQuery}~&~CompYtd=${
						oarData?.comparativePeriod?.yearToDateQuery
					}~&~2CompYtd=${oarData?.secondComparativePeriod?.yearToDateQuery}~&~CurCd=${oarData?.currencyCode}~&~CurType=${
						oarData?.currencyType
					}${businessUnit}~&~AcTyp=${type === 'income' ? 'Revenue~|~Expenses' : 'Assets~|~Liabilities~|~Equity'}`;
				} else {
					queryString = `Report_Key=FSC~&~Component_key=BalCompT~&~AnalysYtd=${oarData?.analysisPeriod?.yearToDateQuery}~&~CompYtd=${
						oarData?.comparativePeriod?.yearToDateQuery
					}~&~CurCd=${oarData?.currencyCode}~&~CurType=${oarData?.currencyType}${businessUnit}~&~AcTyp=${
						type === 'income' ? 'Revenue~|~Expenses' : 'Assets~|~Liabilities~|~Equity'
					}`;
				}
				url += '?state=' + encodeURIComponent(btoa(encodeURIComponent(queryString)));
			}
		}
		return url;
	};

	static getMeasurementBasisUrl = (primaryProject) => {
		let url = '';
		if (primaryProject?.projectId) {
			url = env.getBaseURL('Helix_Platform_Base_Url') + 'app/projects/view-analytics/';
			url += `${primaryProject.projectId}/${primaryProject.helixAnalyzerId}`;
		}
		return url;
	};

	/**
	 * Determines if the current page is in an iframe element.
	 * @returns {boolean} true if the current page is in an iframe element, false otherwise.
	 */
	static pageInIframe() {
		return window.location !== window.parent.location;
	}

	static isDocumentCanvasForm = (documentTypeId) => {
		return CanvasFormsIds.includes(documentTypeId, 0);
	};

	static getKronosUrl(location) {
		let pathNameAfterBase = location?.pathname || locationParentObject.location.pathname;

		if (locationParentObject.basename) {
			pathNameAfterBase = pathNameAfterBase.replace(locationParentObject.basename, '');
		}

		const match = matchRoutes(
			[{path: '/canvasform'}, {path: '/independence'}, {path: '/profile'}, {path: '/canvasform/changes'}, {path: '/canvasform/details'}],
			pathNameAfterBase
		);
		if (Array.isArray(match) && match.length > 0 && match[0].params) {
			const url = match[0].pathname;
			return url;
		}
		return '';
	}

	static getRelativeDate(date) {
		const DIVISIONS = [
			{amount: 7, name: labels.daysAgo},
			{amount: 4.34524, name: labels.weeksAgo},
			{amount: 12, name: labels.monthsAgo},
			{amount: Number.POSITIVE_INFINITY, name: labels.yearsAgo}
		];

		let duration = Math.round((Date.now() - new Date(date).getTime()) / (1000 * 3600 * 24));

		for (let i = 0; i <= DIVISIONS.length; i++) {
			const division = DIVISIONS[i];

			if (duration < division.amount) {
				if (division.name === labels.daysAgo) {
					switch (duration) {
						case 0:
							return labels.today;
						case 1:
							return labels.yesterday;
						default:
							return `${duration} ${labels.daysAgo}`;
					}
				}

				if (division.name === labels.weeksAgo) {
					if (Math.trunc(duration) === 1) {
						return `1 ${labels.weekAgo}`;
					}

					return `${Math.trunc(duration)} ${labels.weeksAgo}`;
				}

				if (division.name === labels.monthsAgo) {
					if (Math.trunc(duration) === 1) {
						return `1 ${labels.monthAgo}`;
					}

					return `${Math.trunc(duration)} ${labels.monthsAgo}`;
				}

				if (division.name === labels.yearsAgo) {
					if (Math.trunc(duration) === 1) {
						return `1 ${labels.yearAgo}`;
					}

					return `${Math.trunc(duration)} ${labels.yearsAgo}`;
				}
			}

			duration /= division.amount;
		}
	}

	static trimText = (text, maxLenght) => {
		if (text.length > maxLenght) {
			return text.substring(0, maxLenght);
		}
		return text;
	};

	static getLastChildFromValidations = (validations) => {
		let deepestObject = null;
		let maxDepth = -1;

		function traverse(obj, depth = 0) {
			if (Array.isArray(obj)) {
				obj.forEach((item) => traverse(item, depth + 1));
			} else if (typeof obj === 'object' && obj !== null) {
				if (depth > maxDepth) {
					maxDepth = depth;
					deepestObject = obj;
				}
				Object.values(obj).forEach((value) => traverse(value, depth + 1));
			}
		}

		traverse(validations);

		return deepestObject;
	};

	static documentHasLeftNavigation = (documentTypeId) => {
		return CanvasFormsWithoutNavigationPanel.indexOf(documentTypeId) === -1;
	};

	static checkValidationsForEstimate = (estimateId, riskId, smartEntityId) => {
		return isNullOrUndefined(riskId) || (isNullOrUndefined(smartEntityId) && smartEntityId !== estimateId);
	};
	static clone = (val) => {
		if (val) {
			return JSON.parse(JSON.stringify(val));
		}
		return val;
	};
	static isEstimateDataValidForDisplay = (riskTypeId) => {
		const riskTypesOfEstimates = riskTypeList.filter((riskType) => {
			return ![riskTypes.SIGNIFICANTRISK, riskTypes.FRAUDRISK, riskTypes.INHERENTRISK].includes(riskType.id);
		});
		return riskTypesOfEstimates.some((x) => x.id == riskTypeId);
	};

	static getRelatedROMMs = (risks = []) => {
		return (risks || []).filter(
			(risk) => risk && (risk.riskTypeId === riskTypes.SIGNIFICANTRISK || risk.riskTypeId === riskTypes.FRAUDRISK || risk.riskTypeId === riskTypes.INHERENTRISK)
		);
	};

	static removeTags(str) {
		if (!str || str === null || str === '') return false;
		else str = str.toString();
		return str.replace(/(<([^>]+)>)/gi, '');
	}

	/**
	 * Loads data from session storage for a given key.
	 * @param {string} key - The key to retrieve the value for from session storage.
	 * @returns {object|null} The object from session storage if found and parsed, otherwise null.
	 */
	static loadStorage = (key) => {
		try {
			const serializedState = sessionStorage.getItem(key);

			if (serializedState === null) {
				return {};
			}

			return JSON.parse(serializedState);
		} catch (err) {
			return {};
		}
	};

	/**
	 * Saves the given state object to session storage for a given key.
	 * @param {string} key - The key under which to store the state.
	 * @param {object} state - The state object to be saved.
	 * @returns {object} - Returns an empty object if an error occurs.
	 * @throws {Error} - Throws an error if the state cannot be serialized to JSON
	 */
	static saveStorage = (key, state) => {
		try {
			const serializedState = JSON.stringify(state);

			sessionStorage.setItem(key, serializedState);
		} catch (err) {
			return {};
		}
	};

	/**
	 * Loads data from session storage for a given key.
	 * @param {string} key - The key to retrieve the value for from session storage.
	 * @returns {object} The object from session storage if found and parsed, otherwise an empty object.
	 * @throws {Error} If parsing the stored value fails.
	 */
	static clearStorage = (key) => {
		try {
			sessionStorage.removeItem(key);
		} catch (err) {
			return {};
		}
	};

	static findSectionById = (headers, sectionId) => {
		for (const header of headers) {
			for (const section of header.sections) {
				if (section.id === sectionId) {
					return section;
				}
			}
		}
		return null; // Return null if no matching section is found
	};

	static removeQueryStringProperties = (url, propertiesToRemove) => {
		// Parse the URL and query string
		const parsedUrl = new URL(url);
		const queryParams = queryString.parse(parsedUrl.search);

		// Remove the specified properties
		propertiesToRemove.forEach((property) => {
			delete queryParams[property];
		});

		// Stringify the updated query parameters
		parsedUrl.search = queryString.stringify(queryParams);
		return parsedUrl.toString();
	};

	static isValidNumber(number) {
		return typeof number === 'number' && Number.isInteger(number) && number > 0;
	}

	static isDocumentEligibleForDaaSCheckOut(document) {
		var documentStatus =
			document?.documentStatusId < DocumentStatus.DaaSCheckoutComplete ||
			document?.documentStatusId > DocumentStatus.DaaSDiscardError ||
			[DocumentStatus.DaaSCheckoutError, DocumentStatus.DaaSCheckinComplete, DocumentStatus.DaaSDiscardComplete].includes(document?.documentStatusId);

		return documentStatus;
	}

	static isDocumentEligibleForDaaSCheckIn(document) {
		return (
			document?.contentContractId === ContentContract.DaaS &&
			[DocumentStatus.DaaSCheckoutComplete, DocumentStatus.DaaSCheckinError, DocumentStatus.DaaSDiscardError].includes(document?.documentStatusId)
		);
	}

	static isDaaSDocumentCheckOutInProgress(document) {
		return document?.documentStatusId === DocumentStatus.DaaSCheckoutInProgress;
	}

	static isDaaSDocumentCheckOutComplete(document) {
		return document?.contentContractId === ContentContract.DaaS && document?.documentStatusId === DocumentStatus.DaaSCheckoutComplete;
	}

	static isDaaSDocumentCheckInInProgress(document) {
		return document?.contentContractId === ContentContract.DaaS && document?.documentStatusId === DocumentStatus.DaaSCheckinInProgress;
	}

	static isDaaSDocumentCheckInError(document) {
		return document?.contentContractId === ContentContract.DaaS && document?.documentStatusId === DocumentStatus.DaaSCheckinError;
	}

	static isDaaSDocumentCheckOutCheckInInProgress(document) {
		return this.isDaaSDocumentCheckOutInProgress(document) || this.isDaaSDocumentCheckInInProgress(document);
	}

	static getKnowledgeAssertionId = (statementTypeId) => {
		switch (statementTypeId) {
			case StatementType.Income:
				return AssertionIds[StatementType.Income].Measurement;
			case StatementType.BalanceSheet:
				return AssertionIds[StatementType.BalanceSheet].Valuation;
			default:
				return AssertionIds[StatementType.Both].MeasurementValuation;
		}
	};

	/**
	 * Validates if the str is a valid URL
	 * @param {string} str - string to validate.
	 * @returns {boolean} - true if the string is a valid URL, false otherwise.
	 * * */
	static validURL = (str) => {
		const pattern = new RegExp(
			'^(https?:\\/\\/)?' + // protocol
				'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
				'((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
				'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
				'(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
				'(\\#[-a-z\\d_]*)?$',
			'i'
		); // fragment locator
		return !!pattern.test(str);
	};

	//Returns the scot types appended eg: Non routine, Estimation

	static getScotTypes = (scotTypes) => {
		let scotType = '';
		if (!scotTypes || scotTypes.length === 0) {
			return labels.notSelected;
		}
		scotTypes.forEach((scotTypeId) => {
			if (scotTypeId !== ScotType.Estimation && scotTypeId !== 0) {
				const result = currentResource.scotTypesNew.find((scopeTypeObject) => scopeTypeObject.value === scotTypeId);
				if (result?.label) {
					scotType += result.label + ', ';
				}
			}
		});
		if (scotType.endsWith(', ')) {
			scotType = scotType.slice(0, -2);
		}
		return scotType === '' ? labels.notSelected : scotType;
	};

	// retrive assertion details based on knowledgeAssertionId and statementTypeId
	static getMappedAssertionDetails = (knowledgeAssertionId, statementTypeId) => {
		if (!knowledgeAssertionId) return [];
		let targetStatementTypeId = statementTypeId ?? StatementType.Both; // Default to Both if not provided

		let originalAssertionName = null;

		// Step 1: Find the original assertion name from the knowledgeAssertionId
		for (const assertions of Object.values(AssertionIds)) {
			for (const [name, id] of Object.entries(assertions)) {
				if (id === knowledgeAssertionId) {
					originalAssertionName = name;
					break;
				}
			}
			if (originalAssertionName) break;
		}

		if (!originalAssertionName) return [];

		// Step 2: Get the mapped assertion names for the target statementTypeId
		let mappedNames = assertionNameMapper[originalAssertionName]?.[targetStatementTypeId];
		if (!mappedNames) return [];

		// Step 3: If mappedNames is 'RightsAndObligations', override statementTypeId to both and re-fetch
		if (mappedNames === RightsAndObligations && targetStatementTypeId === StatementType.Income) {
			targetStatementTypeId = StatementType.Both;
			mappedNames = assertionNameMapper[originalAssertionName]?.[targetStatementTypeId];
			if (!mappedNames) return [];
		}

		// Step 3: Support single or multiple mapped names
		const mappedNameList = Array.isArray(mappedNames) ? mappedNames : [mappedNames]; // Step 4: Get all matching assertion objects

		const matchedAssertions = mappedNameList
			.map((name) => {
				const id = AssertionIds[targetStatementTypeId]?.[name];
				return assertionsFormat.find((assertion) => assertion.id === id);
			})
			.filter(Boolean); // remove undefined
		return matchedAssertions;
	};

	static getPrintUrl(baseDomain, engagementId, documentId, userPreferences) {
		const snapshotUser = `${userPreferences.firstName} ${userPreferences.lastName}`;
		const formPrintUrl =
			baseDomain +
			`print?engagementid=${engagementId}&documentid=${documentId}&snapshotid=${-1}&snapshotuser=${encodeURIComponent(snapshotUser)}&isAutomatedPrint=true`;
		return formPrintUrl;
	}

	static getAresTrackChangesUrl = (documentId, headerId, sectionId, engagementId, taskId, queryParameters, documentTypeId, isTrackChanges) => {
		const _headerId = headerId === -1 ? 'summary' : headerId;

		// As we have Track changes view only on v2.
		let url = this.isDocumentTypeFitV2(documentTypeId) || isTrackChanges ? `/${aresTaskViewRoutes.guidedworkflowV2}` : `/${aresTaskViewRoutes.guidedworkflow}`;

		if (documentId) {
			// Activity page url:
			url += `/${documentId}`;

			if (_headerId && _headerId !== 'summary') {
				url += `/${aresTaskViewRoutes.header}/${_headerId}`;

				if (sectionId) {
					url += `/${aresTaskViewRoutes.section}/${sectionId}`;
				}
			} else if (_headerId === 'summary') {
				url += `/${aresTaskViewRoutes.summary}`;
			}
		}

		url += `?${canvasUrlParams.engagementid}=${engagementId}`;

		if (taskId) {
			url += `&taskId=${taskId}`;
		}

		if (isTrackChanges) {
			queryParameters = {
				...queryParameters,
				istrackchanges: true
			};
		}

		const query = getFilteredQueryParameter(queryParameters);
		if (query) {
			url += this.appendObjectParams(query);
		}
		return url;
	};

	static deepEqual(obj1, obj2) {
		// Base case: if both are strictly equal (primitives or same object reference)
		if (obj1 === obj2) {
			return true;
		}

		// Handle null or non-object types
		if (obj1 == null || typeof obj1 !== 'object' || obj2 == null || typeof obj2 !== 'object') {
			return false;
		}

		// Handle Date objects
		if (obj1 instanceof Date && obj2 instanceof Date) {
			return obj1.getTime() === obj2.getTime();
		}

		// Get keys of both objects
		const keys1 = Object.keys(obj1);
		const keys2 = Object.keys(obj2);

		// Check if they have the same number of keys
		if (keys1.length !== keys2.length) {
			return false;
		}

		// Iterate over keys and recursively compare values
		for (const key of keys1) {
			if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Invert a two-level relation on a single root object:
	 *   root[sourceKey][] contains items, each with [nestedKey][].
	 * Result:
	 *   root[targetKey][] contains unique nested items, each with [reverseNestedKey][] built from the source item.
	 *
	 * Example:
	 *   sourceKey="assertions", nestedKey="risks"  ==>  targetKey="risks", reverseNestedKey="assertions"
	 *
	 * @param {object} root                             The input root object (e.g., account)
	 * @param {object} opts
	 * @param {string} opts.sourceKey                   Key on root holding the source array (e.g., "assertions")
	 * @param {string} opts.nestedKey                   Key on each source item that holds the nested array (e.g., "risks")
	 * @param {string} opts.targetKey                   Key name to place aggregated nested items on root (e.g., "risks")
	 * @param {string} opts.reverseNestedKey            Key name to place aggregated source items inside each nested item (e.g., "assertions")
	 * @param {string} [opts.sourceIdKey="id"]          Unique id field on each source item (used for dedupe in reverse arrays)
	 * @param {string} [opts.nestedIdKey="id"]          Unique id field on each nested item (used for dedupe at root[targetKey])
	 * @param {boolean} [opts.removeOriginal=false]     If true, remove root[sourceKey] in the output
	 * @param {function} [opts.projectSource]           (sourceItem) => object kept inside reverseNestedKey (default: shallow copy)
	 * @param {function} [opts.projectNested]           (nestedItem) => object kept at target level (default: shallow copy)
	 * @returns {object}                                A new root object with inverted relation
	 */
	static invertNestedRelation(root, opts) {
		const {
			sourceKey,
			nestedKey,
			targetKey,
			reverseNestedKey,
			sourceIdKey = 'id',
			nestedIdKey = 'id',
			removeOriginal = false,
			projectSource = (s) => ({...s}),
			projectNested = (n) => ({...n})
		} = opts;

		const out = {...root};
		const sources = Array.isArray(root[sourceKey]) ? root[sourceKey] : [];

		const nestedById = new Map(); // nestedId -> node
		const seenSrcByNested = new Map(); // nestedId -> Set(sourceId)
		const sigSetByNested = new Map(); // nestedId -> Set(signature) for source-without-id

		for (const src of sources) {
			const nestedArr = Array.isArray(src[nestedKey]) ? src[nestedKey] : [];
			for (const n of nestedArr) {
				const nestedId = n?.[nestedIdKey];
				if (nestedId == null) continue;

				if (!nestedById.has(nestedId)) {
					const node = projectNested(n);
					node[reverseNestedKey] = [];
					nestedById.set(nestedId, node);
					seenSrcByNested.set(nestedId, new Set());
					sigSetByNested.set(nestedId, new Set());
				}

				const node = nestedById.get(nestedId);
				const seenSrc = seenSrcByNested.get(nestedId);
				const sigSet = sigSetByNested.get(nestedId);

				const srcId = src?.[sourceIdKey];
				if (srcId == null) {
					const candidate = projectSource(src);
					const sig = JSON.stringify(candidate);
					if (!sigSet.has(sig)) {
						sigSet.add(sig);
						node[reverseNestedKey].push(candidate);
					}
				} else {
					if (!seenSrc.has(srcId)) {
						seenSrc.add(srcId);
						node[reverseNestedKey].push(projectSource(src));
					}
				}
			}
		}

		out[targetKey] = Array.from(nestedById.values());
		if (removeOriginal) delete out[sourceKey];
		return out;
	}

	static getRelativeUrl = (fullUrl) => {
		const {pathname, search} = new URL(fullUrl, window.location.origin);
		if (pathname.startsWith('/forms/')) {
			return `${pathname}${search}`;
		}
		return `/forms/${pathname.substring(1)}${search}`;
	};

	static cfAlphanumSort(array, key, {order = 'asc', locale = undefined, options = {sensitivity: 'base', numeric: true}} = {}) {
		if (!Array.isArray(array) || array.length === 0) {
			return [];
		}

		return array.sort((a, b) =>
			order === 'desc' ? (b[key] || '').localeCompare(a[key] || '', locale, options) : (a[key] || '').localeCompare(b[key] || '', locale, options)
		);
	}

	static getMFEAttributes() {
		let isMFE = false,
			mfeInstanceId = null;
		let memoryRouterMode = false;

		const element = document.querySelector('[data-mfe]');

		if (element) {
			isMFE = true;

			const attributeValue = element.getAttribute('data-mfe');

			if (attributeValue === 'memory') {
				memoryRouterMode = true;
			}
		}

		// Check if the current domain is the Kronos web URL
		if (isMFE) {
			const kronosWebUrl = env.getURL('kronos_web');
			if (kronosWebUrl) {
				const currentDomain = window.location.origin;
				const kronosDomain = new URL(kronosWebUrl).origin;
				if (currentDomain === kronosDomain) {
					isMFE = false;
				}
			}

			mfeInstanceId = document.querySelector('[data-mfe-instance-id]')?.getAttribute('data-mfe-instance-id');
		}

		return [isMFE, memoryRouterMode, mfeInstanceId];
	}

	static getLocalizedControlType = (controlTypeId) => {
		if (!controlTypeId || !Array.isArray(currentResource.controlType)) {
			return labels.notSelectedLabel;
		}

		const matchedControlType = currentResource.controlType.find((item) => item?.value === controlTypeId);
		return matchedControlType?.label || matchedControlType?.title || labels.notSelectedLabel;
	};

	static getLocalizedDesign = (controlDesignEffectivenessTypeID) => {
		if (!controlDesignEffectivenessTypeID || !Array.isArray(currentResource.designEffectivenessType)) {
			return labels.notSelectedLabel;
		}

		const matchedControlDesign = currentResource.designEffectivenessType.find((item) => item?.DesignEffectivenessTypeId === controlDesignEffectivenessTypeID);
		return matchedControlDesign?.DesignEffectivenessTypeName || labels.notSelectedLabel;
	};

	static getLocalizedOperation = (isEffective) => {
		if (isEffective == null) return labels.notSelectedLabel;
		return isEffective ? labels.effectiveLabel : labels.ineffectiveLabel;
	};

	static getDeficiencyDesignEffectivenessLabel = (value) => {
		return value
			? (currentResource.controlEffectivenessType?.find((c) => c.ControlEffectivenessTypeId === value)?.ControlEffectivenessTypeName ?? labels.notSelectedLabel)
			: labels.notSelectedLabel;
	};

	static getDeficiencyOperatingEffectivenessLabel = (value) => {
		return value !== undefined && value !== null
			? value
				? currentResource.controlEffectivenessType?.[0]?.ControlEffectivenessTypeName
				: currentResource.controlEffectivenessType?.[1]?.ControlEffectivenessTypeName
			: labels.notSelectedLabel;
	};

	static isDeficiencyIneffectiveOrNotSelected = (label) => {
		const effectiveLabel = currentResource.controlEffectivenessType?.[0]?.ControlEffectivenessTypeName;
		return label !== effectiveLabel;
	};

	static getFormsFromSummaryResponses(formActions) {
		const summaryResponses = formActions?.summaryResponses;
		const formsFromById = Object.values(summaryResponses?.byId ?? {}).flatMap((response) => response?.forms ?? []);
		const forms = formsFromById.length > 0 ? formsFromById : (summaryResponses?.forms ?? []);
		return forms;
	}

	static getExplanationFromFormActions(formActions, summaryTypeId) {
		const forms = Utility.getFormsFromSummaryResponses(formActions);
		const explanation = forms.flatMap((form) => form?.responses ?? []).find((response) => response?.summaryTypeId === summaryTypeId);

		if (!explanation) return null;

		const explanationText = explanation?.responseText ?? labels.socdSummary298.noExplanationProvided;
		return explanationText;
	}

	static isDeficiencyRelatedDocument(doc, deficiencyId) {
		const isOwnedByDeficiency = doc?.smartEvidenceSourceEntityId === Entity.Deficiency && doc?.smartEvidenceSourceId === deficiencyId;
		const isGuidanceWorkflowDoc =
			doc?.documentTypeId === documentTypes.deficiencyIndividual || doc?.documentTypeId === documentTypes.deficiencyIndividualItgcDeficiency;
		return !(isOwnedByDeficiency && isGuidanceWorkflowDoc);
	}

	static getDocumentTypeId(entityId) {
		switch (entityId) {
			case Entity.ITControl:
				return documentTypes.deficiencyIndividualItgcDeficiency;
			default:
				return documentTypes.deficiencyIndividual;
		}
	}

	static getImpactedControlLockReason(item) {
		switch (item?.controlDeficiencyTypeId) {
			case controlDeficiencyType.InvalidControl:
				return controlDeficiencyType.InvalidControl;
			case controlDeficiencyType.CompensatingControl:
				return item?.isRationaleRequired ? controlDeficiencyType.CompensatingControl : null;
			case controlDeficiencyType.MitigatingControl:
				return item?.isRationaleRequired ? controlDeficiencyType.MitigatingControl : null;
			default:
				return null;
		}
	}
}

export default Utility;
